import { Box, Typography } from "@outschool/backpack";
import { CurrentUserQueryQuery } from "@outschool/gql-frontend-generated";
import { useLocale, useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { ExternalLink } from "@outschool/ui-components-shared";
import {
  CopyToClipboardButton,
  uiContextIds,
  useTopNoticeContext
} from "@outschool/ui-components-website";
import { ModalActionButtons } from "@outschool/ui-legacy-component-library";
import React, { useCallback } from "react";

import * as Routes from "../../shared/Routes";
import PageModal from "./PageModal";
import ReadOnlyCopyInput from "./ReadOnlyCopyInput";

export type AddCalendarModalProps = {
  title: string;
  user: CurrentUserQueryQuery["currentUser"] | undefined;
  type: "parent" | "teacher" | "favorites";
  open: boolean;
  onClose: () => void;
  fullBleed?: boolean;
};

export default function AddCalendarModal({
  title,
  user,
  type,
  open,
  onClose,
  fullBleed
}: AddCalendarModalProps) {
  const locale = useLocale();
  const { t } = useTranslation("client\\components\\AddCalendarModal");
  const { setTopNotice } = useTopNoticeContext();
  const trackEvent = useTrackEvent();

  const trackClick = useCallback(
    (method: $TSFixMe) => {
      trackEvent("Calendar subscribe", { calendarName: type, method });
    },
    [type, trackEvent]
  );

  const copiedMessage = t`Calendar link copied to clipboard.`;
  const handleCopy = useCallback(() => {
    trackClick("copy clipboard");
    setTopNotice(copiedMessage);
  }, [trackClick, setTopNotice, copiedMessage]);

  if (!user) {
    return null;
  }
  const url = Routes.calendarUrl(user.calendar_uid, type);
  return (
    <PageModal open={open} onClose={onClose} fullBleed={fullBleed}>
      <Typography
        variant="h3"
        sx={{
          marginY: "1em"
        }}
      >
        {title}
      </Typography>
      <div>
        <p
          style={{
            marginBottom: "1em"
          }}
        >
          {type === "favorites"
            ? t`See how your favorites fit into your own calendar.`
            : t`See how your classes fit into your own calendar.`}
        </p>
        <p
          style={{
            marginBottom: "1em"
          }}
        >
          {t`When you subscribe to this calendar, the classes will show up in your \
calendar app - and they’ll automatically stay up to date. Your phone \
or computer will ask you to confirm, and then will complete the link \
with your calendar app.`}
        </p>
        <Box
          sx={{
            marginBottom: "1em",
            textAlign: "center"
          }}
        >
          <ExternalLink url={url} onClick={() => trackClick("click subscribe")}>
            {t`Subscribe to calendar...`}
          </ExternalLink>
        </Box>
        <p
          style={{
            marginBottom: "1em"
          }}
        >
          {t`If you use a non-default calendar, just copy the calendar URL below, \
and subscribe to it in your calendar of choice.`}{" "}
          <ExternalLink
            url={"https://support.outschool.com/" + locale + "/articles/614870"}
          >
            {t`Learn more`}
          </ExternalLink>
        </p>
      </div>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row"
        }}
      >
        <ReadOnlyCopyInput
          value={url.replace("webcal", "https")}
          onCopy={handleCopy}
        />
        <CopyToClipboardButton
          value={url.replace("webcal", "https")}
          onCopy={handleCopy}
          uiContextId={uiContextIds.AddCalendarModal}
        />
      </Box>
      <ModalActionButtons
        primaryLabel={t`Done`}
        hideCancelButton={true}
        onPrimary={onClose}
        sx={{ marginTop: "1em" }}
      />
    </PageModal>
  );
}
